import { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { PublicEventService } from './publicEventService';
import { ParticipantCommunityHubService } from './participantCommunityHubService';
import { PeopleSearchService } from './peopleSearchService';
import {
    IGlobalQuickSearchResponse,
    IGlobalQuickSuggestion,
    IGlobalSearchOrganisationResult,
    IGlobalSearchPostResult,
    IGlobalSearchResults,
    IGlobalSearchSection,
} from '../interfaces/IGlobalSearch';
import { IEventList } from '../interfaces/IEventList';
import { ICommunity } from '../interfaces/ICommunityHub';
import { IPeopleSearchResult } from '../interfaces/IPeopleSearch';
import { SearchResultCache } from '../utils/searchResultCache';

export const GLOBAL_SEARCH_PREVIEW_SIZE = 3;
export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2;
export const QUICK_SEARCH_TOTAL_LIMIT = 3;
export const QUICK_SEARCH_CACHE_MAX_ENTRIES = 20;
export const QUICK_SEARCH_CACHE_TTL_MS = 30_000;

const EMPTY_SECTION: IGlobalSearchSection<never> = { items: [], totalCount: 0 };

// Module-level so the cache survives across HeaderSearchBar re-renders/re-mounts
// (a new GlobalSearchService instance is created whenever the axios client changes).
const quickSearchCache = new SearchResultCache<IGlobalQuickSuggestion[]>(
    QUICK_SEARCH_CACHE_MAX_ENTRIES,
    QUICK_SEARCH_CACHE_TTL_MS,
);

/**
 * Converts the backend's raw quick-search response into the flat,
 * interleaved suggestion list the dropdown renders. Pure/stateless so it can
 * be unit tested without an HTTP client.
 */
export function mapQuickSearchResponseToSuggestions(
    response: IGlobalQuickSearchResponse,
    limit: number = QUICK_SEARCH_TOTAL_LIMIT,
): IGlobalQuickSuggestion[] {
    const eventItems: IGlobalQuickSuggestion[] = (response.events ?? []).map((event) => ({
        type: 'event' as const,
        id: event.id,
        label: event.title,
        imageUrl: event.imageUrl,
    }));

    const communityItems: IGlobalQuickSuggestion[] = (response.communities ?? []).map((community) => ({
        type: 'community' as const,
        id: community.id,
        label: community.name,
        subtitle: `${community.memberCount} member${community.memberCount === 1 ? '' : 's'}`,
        imageUrl: community.imageUrl,
        slug: community.slug,
    }));

    const personItems: IGlobalQuickSuggestion[] = (response.people ?? []).map((person) => ({
        type: 'person' as const,
        id: person.userId,
        label: person.displayName,
        subtitle: person.sharedEventCount > 0
            ? `${person.sharedEventCount} shared event${person.sharedEventCount === 1 ? '' : 's'}`
            : person.mutualConnectionCount > 0
                ? `${person.mutualConnectionCount} mutual connection${person.mutualConnectionCount === 1 ? '' : 's'}`
                : undefined,
        imageUrl: person.imageUrl ?? undefined,
    }));

    // Interleave one suggestion per type at a time so the dropdown reads as a
    // single mixed list (event, community, person, ...) rather than grouped blocks.
    const suggestions: IGlobalQuickSuggestion[] = [];
    const buckets = [eventItems, communityItems, personItems];
    for (let index = 0; suggestions.length < limit; index += 1) {
        const before = suggestions.length;
        for (const bucket of buckets) {
            if (suggestions.length >= limit) break;
            if (bucket[index]) suggestions.push(bucket[index]);
        }
        if (suggestions.length === before) break;
    }

    return suggestions;
}

function normaliseSearchText(value: string): string {
    return value.trim().toLowerCase();
}

function getFieldMatchScore(field: string | undefined, query: string): number {
    if (!field) return 0;
    const text = normaliseSearchText(field);
    if (!text) return 0;
    if (text === query) return 120;
    if (text.startsWith(query)) return 80;
    if (text.includes(query)) return 40;
    return 0;
}

function countTokenMatches(field: string | undefined, tokens: string[]): number {
    if (!field || tokens.length === 0) return 0;
    const text = normaliseSearchText(field);
    if (!text) return 0;

    let matches = 0;
    for (const token of tokens) {
        if (text.includes(token)) {
            matches += 1;
        }
    }

    return matches;
}

/**
 * Aggregated search across events, communities, people, posts and organisations.
 * All sources are queried in parallel and each failure degrades to an
 * empty section so one slow/broken source never blocks the others.
 */
export class GlobalSearchService {
    private readonly apiClient: AxiosInstance;
    private readonly eventService: PublicEventService;
    private readonly communityService: ParticipantCommunityHubService;
    private readonly peopleService: PeopleSearchService;

    constructor(applicationClient: AxiosInstance) {
        this.apiClient = applicationClient;
        this.eventService = new PublicEventService(applicationClient);
        this.communityService = new ParticipantCommunityHubService(applicationClient);
        this.peopleService = new PeopleSearchService(applicationClient);
    }

    async search(query: string): Promise<IGlobalSearchResults> {
        const trimmed = query.trim();
        if (trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
            return {
                events: EMPTY_SECTION,
                communities: EMPTY_SECTION,
                people: EMPTY_SECTION,
                posts: EMPTY_SECTION,
                organisations: EMPTY_SECTION,
            };
        }

        const [events, communities, people, posts, organisations] = await Promise.allSettled([
            this.searchEvents(trimmed),
            this.searchCommunities(trimmed),
            this.searchPeople(trimmed),
            this.searchPosts(trimmed),
            this.searchOrganisations(trimmed),
        ]);

        return {
            events: this.unwrap(events, 'events'),
            communities: this.unwrap(communities, 'communities'),
            people: this.unwrap(people, 'people'),
            posts: this.unwrap(posts, 'posts'),
            organisations: this.unwrap(organisations, 'organisations'),
        };
    }

    /**
     * Fast, id/name-only suggestions for the header's typeahead dropdown.
     * Backed by a single combined backend endpoint (rather than a 3-endpoint
     * fan-out), a short-lived result cache for repeat/backspaced queries, and
     * an optional AbortSignal so in-flight requests can be cancelled when the
     * user keeps typing.
     */
    async quickSearch(query: string, options?: { signal?: AbortSignal }): Promise<IGlobalQuickSuggestion[]> {
        const trimmed = normaliseSearchText(query);
        if (trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
            return [];
        }

        const cached = quickSearchCache.get(trimmed);
        if (cached) {
            return cached;
        }

        const response = await this.apiClient.get<IGlobalQuickSearchResponse>('/participant/search/quick', {
            params: { query: trimmed, limit: QUICK_SEARCH_TOTAL_LIMIT },
            signal: options?.signal,
        });

        const suggestions = mapQuickSearchResponseToSuggestions(response.data, QUICK_SEARCH_TOTAL_LIMIT);
        quickSearchCache.set(trimmed, suggestions);
        return suggestions;
    }

    private unwrap<T>(result: PromiseSettledResult<IGlobalSearchSection<T>>, source: string): IGlobalSearchSection<T> {
        if (result.status === 'fulfilled') return result.value;
        console.error(`Global search failed for ${source}:`, result.reason);
        return EMPTY_SECTION;
    }

    private async searchEvents(query: string): Promise<IGlobalSearchSection<IEventList>> {
        const today = dayjs().startOf('day');
        const data = await this.eventService.getAll({
            page: 1,
            pageSize: GLOBAL_SEARCH_PREVIEW_SIZE,
            filters: {
                Title: query,
                // Match the search-events page default window (upcoming year).
                StartDate__gte: today.format('YYYY-MM-DD'),
                EndDate__lte: today.add(365, 'day').format('YYYY-MM-DD'),
            },
            sortBy: 'StartDate',
            sortDesc: false,
        });
        return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
    }

    private async searchCommunities(query: string): Promise<IGlobalSearchSection<ICommunity>> {
        const data = await this.communityService.explorePublicCommunities(1, GLOBAL_SEARCH_PREVIEW_SIZE, query);
        return { items: data.communities ?? [], totalCount: data.totalCount ?? 0 };
    }

    private async searchPeople(query: string): Promise<IGlobalSearchSection<IPeopleSearchResult>> {
        const data = await this.peopleService.search({
            query,
            page: 1,
            pageSize: GLOBAL_SEARCH_PREVIEW_SIZE,
            sortBy: 'relevance',
        });
        return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
    }

    private async searchPosts(query: string): Promise<IGlobalSearchSection<IGlobalSearchPostResult>> {
        if (query.length < 2) {
            return EMPTY_SECTION;
        }

        const lowered = normaliseSearchText(query);
        const tokens = lowered.split(/\s+/).filter((token) => token.length > 1);
        const [feed, joinedCommunities] = await Promise.all([
            this.communityService.getHomeFeed(1, 24, 'all'),
            // Needed to resolve each community post's slug so results can deep-link to the exact post.
            this.communityService.getJoinedCommunities().catch(() => []),
        ]);
        const communitySlugById = new Map(joinedCommunities.map((community) => [community.id, community.slug]));

        const mappedPosts: IGlobalSearchPostResult[] = [];
        for (const item of feed.items) {
            if (item.post) {
                const title = item.post.title?.trim() || item.post.communityName || 'Community post';
                const body = item.post.body || '';
                mappedPosts.push({
                    id: item.post.id,
                    title,
                    excerpt: body.slice(0, 180),
                    authorName: item.post.authorName,
                    createdAt: item.post.createdAt,
                    imageUrl: item.post.imageUrl,
                    communityId: item.post.communityId,
                    communityName: item.post.communityName,
                    communitySlug: communitySlugById.get(item.post.communityId),
                    authorUserId: item.post.authorUserId,
                    postType: 'community',
                });
                continue;
            }

            if (item.userPost) {
                const title = item.userPost.title?.trim() || item.userPost.pod_name || 'Post';
                const body = item.userPost.body || '';
                mappedPosts.push({
                    id: item.userPost.id,
                    title,
                    excerpt: body.slice(0, 180),
                    authorName: item.userPost.authorName,
                    createdAt: item.userPost.createdAt,
                    imageUrl: item.userPost.imageUrl,
                    authorUserId: item.userPost.authorUserId,
                    podId: item.userPost.pod_id,
                    podName: item.userPost.pod_name,
                    postType: 'personal',
                });
            }
        }

        const postItems = mappedPosts
            .map((item) => {
                const score =
                    // Weight title/community heavily to improve perceived relevance.
                    getFieldMatchScore(item.title, lowered) * 4 +
                    getFieldMatchScore(item.communityName, lowered) * 3 +
                    getFieldMatchScore(item.authorName, lowered) * 2 +
                    getFieldMatchScore(item.excerpt, lowered) +
                    getFieldMatchScore(item.podName, lowered) +
                    countTokenMatches(item.title, tokens) * 24 +
                    countTokenMatches(item.communityName, tokens) * 18 +
                    countTokenMatches(item.authorName, tokens) * 12 +
                    countTokenMatches(item.excerpt, tokens) * 4 +
                    countTokenMatches(item.podName, tokens) * 6;

                return { item, score };
            })
            .filter((entry) => entry.score > 0)
            .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score;
                return dayjs(b.item.createdAt).valueOf() - dayjs(a.item.createdAt).valueOf();
            })
            .map((entry) => entry.item);

        return {
            items: postItems.slice(0, GLOBAL_SEARCH_PREVIEW_SIZE),
            totalCount: postItems.length,
        };
    }

    private async searchOrganisations(query: string): Promise<IGlobalSearchSection<IGlobalSearchOrganisationResult>> {
        if (query.length < 2) {
            return EMPTY_SECTION;
        }

        const lowered = normaliseSearchText(query);
        const tokens = lowered.split(/\s+/).filter((token) => token.length > 1);
        const data = await this.communityService.explorePublicCommunities(1, 12, query);

        const organisationsMap = new Map<string, IGlobalSearchOrganisationResult>();
        for (const community of data.communities) {
            const companyId = (community.ownerCompanyId || '').trim();
            const companyName = (community.ownerCompanyName || '').trim();
            if (!companyId || !companyName) continue;

            const nameScore = getFieldMatchScore(companyName, lowered);
            if (nameScore <= 0) continue;

            const existing = organisationsMap.get(companyId);
            if (existing) {
                existing.communityCount += 1;
                continue;
            }

            organisationsMap.set(companyId, {
                companyId,
                companyName,
                companyKey: community.ownerCompanyKey,
                logoUrl: community.ownerCompanyImageUrl,
                communityCount: 1,
            });
        }

        const organisations = Array.from(organisationsMap.values()).sort((a, b) => {
            const aScore = getFieldMatchScore(a.companyName, lowered) * 5 + countTokenMatches(a.companyName, tokens) * 24;
            const bScore = getFieldMatchScore(b.companyName, lowered) * 5 + countTokenMatches(b.companyName, tokens) * 24;
            const scoreDiff = bScore - aScore;
            if (scoreDiff !== 0) return scoreDiff;
            return b.communityCount - a.communityCount;
        });
        return {
            items: organisations.slice(0, GLOBAL_SEARCH_PREVIEW_SIZE),
            totalCount: organisations.length,
        };
    }
}
